import { PlugZap, Settings2, Volume2, VolumeX } from 'lucide-react';
import { useEffect, useState } from 'react';
import { cn } from '@/lib/cn';
import { modelLabel } from '@/session/models';
import { useSession } from '@/session/store';
import { IconButton, Pill } from './ui';

function useClock(): string {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), 15_000);
    return () => window.clearInterval(timer);
  }, []);
  return now.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

const CONNECTION_LABEL = {
  idle: 'Offline',
  connecting: 'Connecting',
  open: 'Live',
} as const;

export function TopBar({ onOpenSettings }: { onOpenSettings: () => void }) {
  const connection = useSession((state) => state.connection);
  const deviceMode = useSession((state) => state.deviceMode);
  const muted = useSession((state) => state.muted);
  const toggleMute = useSession((state) => state.toggleMute);
  const reconnect = useSession((state) => state.reconnect);
  const leaveSession = useSession((state) => state.leaveSession);
  const clock = useClock();

  return (
    <header className="border-line-soft flex h-14 shrink-0 items-center gap-3 border-b px-4 sm:px-6">
      <button
        type="button"
        onClick={leaveSession}
        title="Back to the start screen"
        aria-label="Back to the start screen"
        className="hover:opacity-100 focus-visible:outline-accent/60 rounded transition focus-visible:outline-2 focus-visible:outline-offset-4"
      >
        {/* public/ asset: build the URL from the Vite base (BASE_URL ends in a slash) so it
            resolves under the deployed sub-path, not root. A bare "/dish-logo.svg" would 404
            behind the ALB prefix. */}
        <img
          src={`${import.meta.env.BASE_URL}dish-logo.svg`}
          alt="DISH"
          className="h-4 w-auto opacity-90"
        />
      </button>
      <span className="bg-line h-5 w-px" />
      <span className="text-sm font-semibold tracking-tight">Voice Console</span>

      {/* Both pills describe a conversation in progress, so the start screen keeps the header bare. */}
      {deviceMode && (
        <>
          <Pill tone="accent" className="hidden sm:inline-flex">
            {deviceMode}
          </Pill>
          <ModelPill />
        </>
      )}

      <div className="flex-1" />

      <span className="text-faint hidden text-xs tabular-nums sm:block">{clock}</span>

      <button
        type="button"
        onClick={connection === 'idle' ? reconnect : undefined}
        disabled={connection !== 'idle'}
        title={connection === 'idle' ? 'Reconnect' : undefined}
        className={cn(
          'border-line flex items-center gap-2 rounded-full border py-1 pr-3 pl-2.5 text-xs font-medium transition',
          connection === 'idle' &&
            'hover:border-accent/50 hover:text-text text-muted cursor-pointer',
          connection !== 'idle' && 'text-text',
        )}
      >
        <span
          className={cn(
            'size-1.5 rounded-full',
            connection === 'open' && 'bg-positive',
            connection === 'connecting' && 'bg-warn animate-pulse',
            connection === 'idle' && 'bg-faint',
          )}
        />
        {CONNECTION_LABEL[connection]}
      </button>

      <IconButton
        label={muted ? 'Unmute the agent' : 'Mute the agent'}
        active={muted}
        onClick={toggleMute}
      >
        {muted ? <VolumeX className="size-4" /> : <Volume2 className="size-4" />}
      </IconButton>

      <IconButton label="Connection settings" onClick={onOpenSettings}>
        <Settings2 className="size-4" />
      </IconButton>

      {connection === 'open' && (
        <IconButton label="Sign in again" onClick={reconnect}>
          <PlugZap className="size-4" />
        </IconButton>
      )}
    </header>
  );
}

/**
 * Which model is answering: the one the open session signed in with, or — with no session — the one
 * the next sign-in will ask for. The two differ exactly while a tester has picked a model and not yet
 * reconnected, which the title says rather than the pill quietly showing the wrong one.
 */
function ModelPill() {
  const modelId = useSession((state) => state.modelId);
  const sessionModelId = useSession((state) => state.sessionModelId);

  const live = sessionModelId ?? modelId;
  const pending = sessionModelId !== null && sessionModelId !== modelId;

  return (
    <Pill
      tone={pending ? 'locked' : 'neutral'}
      className="hidden md:inline-flex"
      title={
        pending
          ? `This session is on ${modelLabel(live)}. Reconnect to sign in on ${modelLabel(modelId)}.`
          : `The model this session signs in on: ${modelLabel(live)}`
      }
    >
      {modelLabel(live)}
      {pending && ` → ${modelLabel(modelId)}`}
    </Pill>
  );
}
